// Negozio.tsx — lo Store (/negozio). v030 (richieste del proprietario): un solo elenco con TUTTI i filtri (niente più
// schede «Da prendere» / «La mia collezione»), così si possono provare tutti. Su ogni filtro una chip: Gratis, Silver,
// Gold o il prezzo in stelle (⭐); quelli già tuoi hanno «✓ Tuo». In cima due riquadri (quanti filtri ci sono, quanti
// sono tuoi) e «Filtra» per categoria, per «miei / da prendere» e per tipo. Toccando il prezzo si apre un menu: se
// hai abbastanza stelle lo prendi, altrimenti vedi quante ne mancano e come guadagnarle (solo fatti veri, visti dal
// server). Le carte entrano a cascata, con qualche scintilla; si caricano a gruppi scorrendo.
// Aperto con /negozio#collezione si parte con «I miei».
import { vibra } from "../servizi/nativo.ts";
import { Palco } from "../componenti/Palco.tsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Finestra } from "../componenti/Finestra.tsx";
import { aggiornaIo, useAccount } from "../servizi/account.ts";
import { ErroreRete } from "../servizi/api.ts";
import { filtriDisponibili, profiloFiltri, type FiltroDisponibile } from "../servizi/filtri.ts";
import { compraFiltro, leggiCrediti, leggiRegoleStelle, leggiStatistiche, type MovimentoCrediti, type RegoleStelle } from "../servizi/social.ts";
import { Distintivi } from "../componenti/Distintivi.tsx";
import { testiFiltro } from "../filtri/registro.ts";
import { IconaFiltro } from "../componenti/IconaFiltro.tsx";
import { accessoFiltro, type Accesso } from "../filtri/accesso.ts";
import { CATEGORIE_FILTRO, type CategoriaFiltro } from "../filtri/tipi.ts";
import { vai } from "../servizi/percorso.ts";
import { IMP_APP, PERCORSO_ACCESSO, PERCORSO_PIANI, PREFERENZE } from "../costanti.ts";
import { leggiPreferenza } from "../servizi/preferenze.ts";
import { eBooleano, leggiImpostazione } from "../servizi/impostazioni.ts";
import { Indietro } from "../componenti/Indietro.tsx";
import { AnteprimaFiltro, type FiltroInAnteprima } from "./AnteprimaFiltro.tsx";
import { segnaDesiderato } from "../servizi/obiettivo.ts";
import { useLuceProvami } from "./luceProvami.ts";
import { impostaPesi, pesoFiltro, telefonoLento, type VoceIndice } from "../filtri/potenza.ts";
import { GuidaLuci, serveGuida } from "../componenti/Guida.tsx";

/** Che cosa dice la chip in alto a destra della carta. */
type Tipo = "gratis" | "silver" | "gold" | "stelle";
/** v046: l'ordine della vetrina (0 = in cima). */
const VETRINA: Record<Tipo, number> = { gold: 0, stelle: 0, silver: 1, gratis: 2 };

interface Voce extends FiltroDisponibile {
  nome: string;
  descrizione: string;
  categoria: CategoriaFiltro;
  accesso: Accesso;
  tipo: Tipo;
  tuo: boolean;
}

type Possesso = "tutti" | "miei" | "da_prendere";
/** Quante carte si mostrano all'inizio e a ogni gruppo nuovo (scorrendo). */
const GRUPPO = 36;

/** v033: di quanto bisogna scorrere perché il menu del prezzo si chiuda. */
const SCORRI_CHIUDE_PX = 30;

export function Negozio({ onIndietro }: { onIndietro: () => void }) {
  const { t, i18n } = useTranslation();
  const account = useAccount();
  const io = account.tipo === "registrato" ? account.io : null;
  const piano = io?.piano ?? "base";
  const goldEsclusivi = leggiImpostazione(IMP_APP.goldFiltriEsclusivi, true, eBooleano);
  const [voci, setVoci] = useState<Voce[] | null>(null);
  const [regole, setRegole] = useState<RegoleStelle | null>(null);
  const [anteprima, setAnteprima] = useState<{ elenco: FiltroInAnteprima[]; inizio: number } | null>(null);
  const [movimenti, setMovimenti] = useState<MovimentoCrediti[]>([]);
  const [scelto, setScelto] = useState<Voce | null>(null);
  /** il menu del prezzo: quale filtro e dove (posizione del pulsante) */
  const [menu, setMenu] = useState<{ v: Voce; x: number; y: number; sopra: boolean } | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [fatto, setFatto] = useState<string | null>(null);
  const [invio, setInvio] = useState(false);
  const [quanti, setQuanti] = useState(GRUPPO);
  const [pannelloFiltri, setPannelloFiltri] = useState(false);
  const [categoria, setCategoria] = useState<CategoriaFiltro | "tutte">("tutte");
  const [possesso, setPossesso] = useState<Possesso>(() => (typeof window !== "undefined" && window.location.hash === "#collezione" ? "miei" : "tutti"));
  const [tipo, setTipo] = useState<Tipo | "tutti">("tutti");
  const fondo = useRef<HTMLDivElement>(null);

  const carica = useCallback(async () => {
    const [filtri, indice] = await Promise.all([filtriDisponibili(), import("../filtri/indice/indice.json")]);
    impostaPesi(indice.default as unknown as VoceIndice[]);
    const info = new Map((indice.default as unknown as { id: string; categoria: CategoriaFiltro }[]).map((x) => [x.id, x]));
    const profilo = profiloFiltri();
    const testi = await Promise.all(filtri.map((f) => testiFiltro(f.id, i18n.language)));
    setVoci(
      filtri.map((f, i) => {
        const accesso = accessoFiltro(f, profilo);
        const tipo: Tipo = f.piano === "base" ? "gratis" : f.piano === "silver" ? "silver" : f.soloCrediti || !goldEsclusivi ? "stelle" : "gold";
        return { ...f, ...testi[i]!, categoria: info.get(f.id)?.categoria ?? "testa", accesso, tipo, tuo: accesso === "libero" };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, piano, io?.posseduti.length, io?.tester, goldEsclusivi]);

  useEffect(() => {
    void carica().catch(() => setVoci([]));
  }, [carica]);
  useEffect(() => {
    void leggiRegoleStelle().then(setRegole).catch(() => undefined);
  }, []);
  // v037: quante persone ho portato (i distintivi)
  const [portati, setPortati] = useState<number | null>(null);
  useEffect(() => {
    if (io) void leggiStatistiche().then((s) => setPortati(s.portati ?? 0)).catch(() => undefined);
  }, [io?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (io) void leggiCrediti().then((c) => setMovimenti(c.movimenti)).catch(() => undefined);
  }, [io?.crediti]); // eslint-disable-line react-hooks/exhaustive-deps

  const crediti = io?.crediti ?? 0;
  const lento = telefonoLento();
  const tutti = voci ?? [];
  const miei = tutti.filter((v) => v.tuo);
  // il prossimo obiettivo: il filtro meno caro che non hai ancora
  const daPrendere = tutti.filter((v) => !v.tuo).sort((a, b) => a.prezzoCrediti - b.prezzoCrediti || a.numero - b.numero);
  // v045: prima il filtro provato per ultimo (se non è tuo e ti mancano crediti), poi il meno caro
  const desiderato = leggiPreferenza(PREFERENZE.filtroDesiderato);
  const prossimo = daPrendere.find((v) => v.id === desiderato && v.prezzoCrediti > crediti) ?? daPrendere.find((v) => v.prezzoCrediti > crediti) ?? null;
  const giaPrendibili = daPrendere.filter((v) => v.prezzoCrediti <= crediti).length;

  const visibili = useMemo(
    () =>
      tutti
        .filter((v) => categoria === "tutte" || v.categoria === categoria)
        .filter((v) => (possesso === "tutti" ? true : possesso === "miei" ? v.tuo : !v.tuo))
        .filter((v) => tipo === "tutti" || v.tipo === tipo)
        // v046 (il proprietario): in vetrina prima i più belli — esclusivi Gold e a crediti mescolati, dai più ricchi —,
        // poi i Silver, poi i gratuiti; in fondo quelli già tuoi. A parità, numero di catalogo.
        .sort((a, b) => Number(a.tuo) - Number(b.tuo) || VETRINA[a.tipo] - VETRINA[b.tipo] || b.prezzoCrediti - a.prezzoCrediti || a.numero - b.numero),
    [tutti, categoria, possesso, tipo, crediti],
  );
  const filtriAttivi = (categoria !== "tutte" ? 1 : 0) + (possesso !== "tutti" ? 1 : 0) + (tipo !== "tutti" ? 1 : 0);

  // un gruppo nuovo di carte quando si arriva in fondo
  useEffect(() => setQuanti(GRUPPO), [categoria, possesso, tipo]);
  // v033: le carte entrano quando arrivano sullo schermo (prima entravano tutte all'apertura, anche quelle sotto,
  // e scorrendo non si vedeva niente). Quelle che arrivano insieme entrano una dopo l'altra.
  const griglia = useRef<HTMLUListElement>(null);
  // v045: la prima volta la guida con le luci; poi, se si resta fermi, un «Provalo» a caso si illumina
  const [guida, setGuida] = useState(() => serveGuida("store"));
  useLuceProvami(griglia, !!voci?.length && !guida && !anteprima);
  useEffect(() => {
    const ul = griglia.current;
    if (!ul) return;
    const carte = [...ul.querySelectorAll<HTMLElement>(".ld-carta-store:not([data-entra])")];
    if (typeof IntersectionObserver === "undefined") {
      carte.forEach((c) => c.setAttribute("data-entra", ""));
      return;
    }
    const os = new IntersectionObserver(
      (voci) => {
        let k = 0;
        for (const v of voci) {
          if (!v.isIntersecting) continue;
          const el = v.target as HTMLElement;
          el.style.animationDelay = `${Math.min(k++, 8) * 70}ms`;
          el.setAttribute("data-entra", "");
          os.unobserve(el);
        }
      },
      { rootMargin: "0px 0px -8% 0px" },
    );
    carte.forEach((c) => os.observe(c));
    return () => os.disconnect();
  });
  useEffect(() => {
    const el = fondo.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const os = new IntersectionObserver((e) => e.some((x) => x.isIntersecting) && setQuanti((n) => n + GRUPPO), { rootMargin: "400px" });
    os.observe(el);
    return () => os.disconnect();
  }, [voci]);

  // il menu del prezzo si chiude a ogni tocco (anche sul menu stesso: lo ha chiesto il proprietario) e scorrendo
  useEffect(() => {
    if (!menu) return;
    const chiudi = () => setMenu(null);
    // v033: scorrendo davvero (più di SCORRI_CHIUDE_PX); i piccoli spostamenti della pagina (carte che si caricano
    // sotto) non lo chiudono più
    const y0 = window.scrollY;
    const scorri = () => Math.abs(window.scrollY - y0) > SCORRI_CHIUDE_PX && chiudi();
    const t0 = window.setTimeout(() => {
      window.addEventListener("click", chiudi);
      window.addEventListener("scroll", scorri, { passive: true });
      window.addEventListener("keydown", chiudi);
    }, 0);
    return () => {
      window.clearTimeout(t0);
      window.removeEventListener("click", chiudi);
      window.removeEventListener("scroll", scorri);
      window.removeEventListener("keydown", chiudi);
    };
  }, [menu]);

  const apriAnteprima = (v: Voce) => {
    if (!v.tuo) segnaDesiderato(v.id);
    setAnteprima({
      elenco: visibili.map((x) => ({ id: x.id, nome: x.nome, descrizione: x.descrizione, prezzoCrediti: x.prezzoCrediti, tuo: x.tuo })),
      inizio: Math.max(0, visibili.findIndex((x) => x.id === v.id)),
    });
  };

  const compra = async (v: Voce) => {
    setInvio(true);
    setErrore(null);
    try {
      await compraFiltro(v.id);
      await aggiornaIo();
      vibra("successo");
      setFatto(v.nome);
      setScelto(null);
    } catch (e) {
      setErrore(e instanceof ErroreRete ? e.codice : "generico");
    } finally {
      setInvio(false);
    }
  };

  const chip = (v: Voce) =>
    v.tuo ? (
      <span className="ld-chip-filtro ld-chip-tuo">✓ {t("store.chip_tuo")}</span>
    ) : v.tipo === "stelle" ? null : (
      <span className={`ld-chip-filtro ld-chip-${v.tipo}`}>{t(`store.tipo_${v.tipo}`)}</span>
    );

  return (
    <div className="pagina pagina-account ld-pagina store">
      <header className="ld-barra">
        <Indietro onClick={onIndietro} />
        <span />
      </header>
      <main className="account-centro">
        <Palco mascotte="caso" colore="rosa" titolo={t("store.titolo")} sottotitolo={t("store.testo")} />

        <section className="scheda store-saldo" aria-label={t("social.crediti")}>
          {io ? (
            <>
              <p className="saldo-crediti ld-saldo-stelle" data-crediti={crediti}>
                {t("social.crediti_saldo", { n: crediti })}
              </p>
              {prossimo ? (
                <div className="store-obiettivo">
                  <p className="nota">{t("store.mancano", { n: prossimo.prezzoCrediti - crediti, nome: prossimo.nome })}</p>
                  <span className="barra-avanzamento" role="progressbar" aria-valuemin={0} aria-valuemax={prossimo.prezzoCrediti} aria-valuenow={crediti}>
                    <span style={{ inlineSize: `${Math.round(Math.min(1, crediti / prossimo.prezzoCrediti) * 100)}%` }} />
                  </span>
                </div>
              ) : null}
              {giaPrendibili > 0 && <p className="nota store-puoi">{t("store.puoi_prendere")}</p>}
            </>
          ) : (
            <button type="button" className="pulsante pulsante-primario" onClick={() => vai(PERCORSO_ACCESSO)}>
              {t("social.negozio_accedi")}
            </button>
          )}
          {io && portati !== null && <Distintivi portati={portati} amico={regole?.attivi ? regole.amicoNuovo : undefined} />}
          {regole && (
            <details className="ld-regole-apri">
              <summary className="pulsante pulsante-secondario pulsante-piccolo">{t("store.guadagna_titolo")}</summary>
              <Regole regole={regole} senzaTitolo />
            </details>
          )}
        </section>

        {fatto && (
          <p className="nota ld-comprato" role="status">
            ✨ {t("social.comprato", { nome: fatto })}
          </p>
        )}

        {voci === null ? (
          // v037: mentre arriva il catalogo, le sagome delle carte (si vede subito com'è lo Store)
          <ul className="ld-griglia-store ld-store-sagome" aria-busy="true" aria-label={t("generale.caricamento")}>
            {Array.from({ length: 6 }, (_, i) => (
              <li key={i} className="ld-carta-sagoma" aria-hidden="true">
                <span />
                <span />
                <span />
              </li>
            ))}
          </ul>
        ) : (
          <>
            <dl className="ld-store-conti">
              <div>
                <dt>{t("store.totale")}</dt>
                <dd>{tutti.length}</dd>
              </div>
              <div data-miei="true">
                <dt>{t("store.tuoi")}</dt>
                <dd>{miei.length}</dd>
              </div>
            </dl>

            <div className="ld-store-barra">
              <button type="button" className="pulsante pulsante-piccolo ld-store-filtra" aria-expanded={pannelloFiltri} onClick={() => setPannelloFiltri((x) => !x)}>
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path d="M4 6h16M7 12h10M10 18h4" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                </svg>
                {t("store.filtra")}
                {filtriAttivi > 0 && <span className="ld-store-numero">{filtriAttivi}</span>}
              </button>
              <span className="nota" role="status">
                {t("store.risultati", { n: visibili.length })}
              </span>
            </div>

            {pannelloFiltri && (
              <section className="ld-store-filtri" aria-label={t("store.filtra")}>
                <Gruppo titolo={t("store.gruppo_possesso")} valore={possesso} onValore={(x) => setPossesso(x as Possesso)} voci={(["tutti", "miei", "da_prendere"] as const).map((k) => [k, t(`store.possesso_${k}`)])} />
                <Gruppo
                  titolo={t("store.gruppo_tipo")}
                  valore={tipo}
                  onValore={(x) => setTipo(x as Tipo | "tutti")}
                  voci={[["tutti", t("store.possesso_tutti")], ...(["gratis", "silver", "gold", "stelle"] as const).map((k): [string, string] => [k, t(`store.tipo_${k}`)])]}
                />
                <Gruppo
                  titolo={t("store.gruppo_categoria")}
                  valore={categoria}
                  onValore={(x) => setCategoria(x as CategoriaFiltro | "tutte")}
                  voci={[["tutte", t("store.cat_tutte")], ...CATEGORIE_FILTRO.map((c): [string, string] => [c, t(`filtri.categoria_${c}`)])]}
                />
                {filtriAttivi > 0 && (
                  <button
                    type="button"
                    className="pulsante-link"
                    onClick={() => {
                      setCategoria("tutte");
                      setPossesso("tutti");
                      setTipo("tutti");
                    }}
                  >
                    {t("store.azzera")}
                  </button>
                )}
              </section>
            )}

            {visibili.length === 0 ? (
              <p className="nota">{t("store.nessuno")}</p>
            ) : (
              <ul className="ld-griglia-store" ref={griglia}>
                {visibili.slice(0, quanti).map((v) => (
                  <li key={v.id} className="carta-negozio ld-carta-store" data-filtro={v.id} data-tuo={v.tuo} data-tipo={v.tipo} data-basta={!v.tuo && !!io && crediti >= v.prezzoCrediti}>
                    {chip(v)}
                    <button type="button" className="ld-carta-prova" tabIndex={-1} aria-hidden="true" title={v.descrizione} onClick={() => apriAnteprima(v)}>
                      <span className="ld-carta-alone" aria-hidden="true" />
                      <IconaFiltro id={v.id} nome={v.nome} dimensione={96} />
                      {(v.tuo || v.tipo === "gold" || (!!io && crediti >= v.prezzoCrediti)) && <Scintille />}
                    </button>
                    <span className="carta-filtro-nome">{v.nome}</span>
                    {/* v046: sui telefoni più lenti si dice quali filtri vanno più fluidi (mai «questo è lento») */}
                    {lento && pesoFiltro(v.id) === "leggero" && <span className="ld-fluido">⚡ {t("filtri.fluido")}</span>}
                    {/* v037: «Provalo» di nuovo visibile (il proprietario: «è fondamentale»); anche toccare l'icona apre la prova */}
                    <div className="ld-carta-azioni">
                    <button type="button" className="ld-carta-prova-pulsante" aria-label={`${t("store.prova")}: ${v.nome}`} data-luce-testo={t("store.provami")} onClick={() => apriAnteprima(v)}>
                      {t("store.prova")}
                    </button>
                    {!v.tuo && (
                      <button
                        type="button"
                        className="ld-prezzo-stelle"
                        data-basta={!!io && crediti >= v.prezzoCrediti}
                        aria-haspopup="menu"
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect();
                          const sopra = r.bottom + 260 > window.innerHeight;
                          setMenu({ v, x: Math.min(Math.max(r.left + r.width / 2, 150), window.innerWidth - 150), y: sopra ? r.top : r.bottom, sopra });
                        }}
                      >
                        {t("social.prezzo_crediti", { n: v.prezzoCrediti })}
                      </button>
                    )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div ref={fondo} aria-hidden="true" />
          </>
        )}

        {io && movimenti.length > 0 && (
          <section className="scheda-account" aria-labelledby="titolo-movimenti">
            <h2 className="scheda-titolo" id="titolo-movimenti">
              {t("social.movimenti")}
            </h2>
            <ul className="elenco-movimenti">
              {movimenti.slice(0, 20).map((m, i) => (
                <li key={i} data-stato={m.stato}>
                  <span>{t(`social.motivo_${m.motivo}`)}</span>
                  <span className="nota">{m.stato !== "confermato" ? t(`social.stato_${m.stato}`) : new Date(m.il).toLocaleDateString(i18n.language)}</span>
                  <strong>{m.delta > 0 ? `+${m.delta}` : m.delta} ⭐</strong>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>

      {menu &&
        createPortal(
        <div
          className="ld-menu-stelle"
          role="menu"
          data-sopra={menu.sopra}
          style={{ left: menu.x, top: menu.sopra ? undefined : menu.y + 8, bottom: menu.sopra ? window.innerHeight - menu.y + 8 : undefined }}
        >
          <strong className="ld-menu-titolo">{menu.v.nome}</strong>
          {io && crediti >= menu.v.prezzoCrediti ? (
            <button
              type="button"
              role="menuitem"
              className="ld-cta ld-cta-centrata"
              onClick={() => {
                setErrore(null);
                setScelto(menu.v);
              }}
            >
              {t("store.prendi", { n: menu.v.prezzoCrediti })}
            </button>
          ) : (
            <p className="nota">{io ? t("store.ti_mancano", { n: menu.v.prezzoCrediti - crediti }) : t("social.negozio_accedi")}</p>
          )}
          {(menu.v.tipo === "silver" || menu.v.tipo === "gold") && (
            <button type="button" role="menuitem" className="pulsante pulsante-piccolo" onClick={() => vai(PERCORSO_PIANI)}>
              {t(menu.v.tipo === "silver" ? "store.incluso_silver" : "store.incluso_gold")}
            </button>
          )}
          {regole && !(io && crediti >= menu.v.prezzoCrediti) && <Regole regole={regole} piccole />}
        </div>,
          document.body,
        )}

      {guida && !!voci?.length && (
        <GuidaLuci
          chiave="store"
          passi={[
            { sel: ".store-saldo", testo: io ? t("guida.store_saldo") : t("guida.store_saldo_ospite") },
            { sel: ".ld-griglia-store:not(.ld-store-sagome) .ld-carta-prova-pulsante", testo: t("guida.store_prova") },
          ]}
          onFine={() => setGuida(false)}
        />
      )}
      {anteprima && (
        <AnteprimaFiltro
          elenco={anteprima.elenco}
          inizio={anteprima.inizio}
          onChiudi={() => setAnteprima(null)}
          onPrendi={
            io
              ? (f) => {
                  const v = voci?.find((x) => x.id === f.id);
                  setAnteprima(null);
                  if (v) {
                    setErrore(null);
                    setScelto(v);
                  }
                }
              : null
          }
        />
      )}

      <Finestra aperta={scelto !== null} titolo={scelto?.nome ?? ""} onChiudi={() => setScelto(null)}>
        {scelto && io && (
          <>
            <p>{t("social.compra_conferma", { nome: scelto.nome, n: scelto.prezzoCrediti })}</p>
            {io.crediti < scelto.prezzoCrediti && <p className="nota">{t("store.mancano", { n: scelto.prezzoCrediti - io.crediti, nome: scelto.nome })}</p>}
            {errore && (
              <p className="admin-errore" role="alert">
                {t(`social.errore_${errore}`, { defaultValue: t("account.errore_generico") })}
              </p>
            )}
            <div className="azioni-riga">
              <button type="button" className="pulsante pulsante-secondario" onClick={() => setScelto(null)}>
                {t("generale.annulla")}
              </button>
              <button type="button" className="pulsante pulsante-primario" disabled={invio || io.crediti < scelto.prezzoCrediti} onClick={() => void compra(scelto)}>
                {t("social.compra")}
              </button>
            </div>
          </>
        )}
      </Finestra>
    </div>
  );
}

/** Come si guadagnano le stelle: solo fatti che il server vede (scelta del proprietario, v030). */
function Regole({ regole: r, piccole = false, senzaTitolo = false }: { regole: RegoleStelle; piccole?: boolean; senzaTitolo?: boolean }) {
  const { t } = useTranslation();
  if (!r.attivi) return null;
  const righe: [string, number][] = [
    [t("store.regola_amico"), r.amicoNuovo],
    [t("store.regola_prima"), r.primaPartitaAmico],
    [t("store.regola_giocatore", { n: r.amicoAvversari }), r.amicoGiocatore],
    ...(r.partiteOgni > 0 && r.partitePremio > 0 ? [[t("store.regola_partite", { n: r.partiteOgni }), r.partitePremio] as [string, number]] : []),
    // v037
    [t("store.regola_video"), r.amicoNuovo],
    [t("store.regola_condivisione"), r.condivisione ?? 0],
    [t("store.regola_giorno"), r.giorno ?? 0],
  ];
  return (
    <div className={`ld-regole-stelle${piccole ? " ld-regole-piccole" : ""}`}>
      {!senzaTitolo && <strong>{t("store.guadagna_titolo")}</strong>}
      <ul>
        {righe
          .filter(([, n]) => n > 0)
          .map(([testo, n]) => (
            <li key={testo}>
              <span>{testo}</span>
              <b>+{n} ⭐</b>
            </li>
          ))}
      </ul>
      {r.tettoGiorno > 0 && <span className="nota">{t("store.regola_tetto", { n: r.tettoGiorno })}</span>}
    </div>
  );
}

function Gruppo({ titolo, valore, voci, onValore }: { titolo: string; valore: string; voci: [string, string][]; onValore: (v: string) => void }) {
  return (
    <div className="ld-store-gruppo" role="radiogroup" aria-label={titolo}>
      <span className="etichetta">{titolo}</span>
      <div className="ld-store-scelte">
        {voci.map(([k, testo]) => (
          <button key={k} type="button" role="radio" aria-checked={valore === k} className="ld-store-scelta" onClick={() => onValore(k)}>
            {testo}
          </button>
        ))}
      </div>
    </div>
  );
}

function Scintille() {
  return (
    <span className="ld-scintille" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
}
